import { Component, computed, inject, signal } from '@angular/core';
import { rxResource } from '@angular/core/rxjs-interop';
import {
  DokaSearchField,
  DokaTable,
  DokaThead,
  DokaTr,
  DokaTh,
  DokaTbody,
  DokaTd,
  DokaIcon,
  DokaModalController,
} from '@doka/angular-core';
import { I18NextEagerPipe } from 'angular-i18next';
import { ICountryModel, LocationsClient } from 'src/api/services/generated/api-clients';
import { SpinnerComponent } from 'src/app/shared/components/spinner/spinner.component';
import { ToastService } from 'src/app/shared/services/toast.service';
import { EditCountrySlideOverComponent } from './edit-country-slide-over/edit-country-slide-over.component';
import { UserInfoService } from 'src/app/shared/services/user-info.service';

@Component({
  selector: 'app-master-data-countries',
  templateUrl: './master-data-countries.component.html',
  imports: [
    I18NextEagerPipe,
    DokaSearchField,
    DokaTable,
    DokaThead,
    DokaTr,
    DokaTh,
    DokaTbody,
    DokaTd,
    SpinnerComponent,
    DokaIcon,
  ],
})
export class MasterDataCountriesComponent {
  private readonly locationsService = inject(LocationsClient);
  private readonly modalCtrl = inject(DokaModalController);
  private readonly toastService = inject(ToastService);
  private readonly userInfoService = inject(UserInfoService);

  protected readonly countries = rxResource({
    stream: () => this.locationsService.getCountries(undefined),
  });

  protected searchText = signal('');

  protected readonly filteredCountries = computed(() => {
    const search = this.searchText().toLowerCase();
    const countries = this.countries.value()?.data ?? [];

    return countries.filter(
      (country) =>
        country.countryName.toLowerCase().includes(search) ||
        country.countryCode.toLowerCase().includes(search) ||
        country.divisionName.toLowerCase().includes(search),
    );
  });

  protected async editCountry(country: ICountryModel) {
    const modal = await this.modalCtrl.create(
      {
        component: EditCountrySlideOverComponent,
        componentProps: { country: signal(country).asReadonly() },
      },
      'slide-over',
    );

    await modal.present();

    const { data, role } = await modal.onDidDismiss();

    if (role === 'saved' && data) {
      this.countries.reload();
      this.toastService.showSuccess('MasterData.CountriesGrid.SuccessToastEdit');
    }
  }

  protected canEdit(country: ICountryModel): boolean {
    return this.userInfoService.isSuperAdmin() || this.userInfoService.regionIds().includes(country.regionId);
  }
}
